import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import {
  CheckCircle, Star, Buildings, Truck, Rocket, ShieldCheck, Lightning,
  WhatsappLogo, Copy, ArrowRight, CreditCard, Money,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    key: "acerto_rapido_autonomo_monthly",
    icon: Truck,
    role: "autonomo",
    audience: "MOTORISTA",
    label: "Autônomo · Mensal",
    price: "R$ 25,90",
    period: "por mês",
    highlight: false,
    perks: ["Até 5 veículos", "Todos os relatórios + PDF", "PIX à vista mensal", "Convide sua empresa e ganhe 50% OFF"],
  },
  {
    key: "acerto_rapido_autonomo_annual",
    pix_key: "acerto_rapido_autonomo_annual_pix",
    pix_price: "R$ 233,10",
    pix_savings: "Economize R$ 77,70",
    icon: Truck,
    role: "autonomo",
    audience: "MOTORISTA",
    label: "Autônomo · Anual",
    price: "R$ 310,80",
    period: "por ano",
    highlight: false,
    savings: null,
    perks: ["Tudo do Mensal", "Cartão em até 3x sem juros", "🔥 PIX à vista com 25% OFF"],
  },
  {
    key: "acerto_rapido_monthly",
    icon: Buildings,
    role: "company",
    audience: "EMPRESA",
    label: "Empresa · Mensal",
    price: "R$ 59,90",
    period: "por mês",
    highlight: true,
    perks: ["Até 150 veículos + 150 motoristas", "GPS em tempo real dos caminhões", "Ranking motoristas + análise km/L", "Excel mensal pro contador"],
  },
  {
    key: "acerto_rapido_annual",
    pix_key: "acerto_rapido_annual_pix",
    pix_price: "R$ 539,10",
    pix_savings: "Economize R$ 179,70",
    icon: Buildings,
    role: "company",
    audience: "EMPRESA",
    label: "Empresa · Anual",
    price: "R$ 718,80",
    period: "por ano",
    highlight: false,
    savings: null,
    perks: ["Tudo do Empresa Mensal", "Cartão em até 3x sem juros", "🔥 PIX à vista com 25% OFF"],
  },
];

export default function PublicSubscribe() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(params.get("plan") || "acerto_rapido_monthly");
  const [pixDiscount, setPixDiscount] = useState(false);  // usar variante _pix (25% OFF)
  const refCode = (params.get("ref") || "").toUpperCase().trim();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    name: "",
    phone: "",
  });

  const plan = useMemo(() => PLANS.find((p) => p.key === selected) || PLANS[2], [selected]);
  const effectiveKey = pixDiscount && plan.pix_key ? plan.pix_key : plan.key;
  const effectivePrice = pixDiscount && plan.pix_price ? plan.pix_price : plan.price;

  // Fire click tracking once on mount
  useEffect(() => {
    axios.post(`${BACKEND_URL}/api/public/track-click`, {
      ref_code: refCode,
      plan_hint: selected,
      path: "/assinar",
      referrer: (typeof document !== "undefined" ? document.referrer : "") || "",
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset PIX discount when switching to a plan without PIX variant
  useEffect(() => {
    if (!plan.pix_key) setPixDiscount(false);
  }, [plan.pix_key]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const goPay = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.email.includes("@")) {
      toast.error("Informe um e-mail válido");
      return;
    }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${BACKEND_URL}/api/public/payments/checkout`, {
        origin_url: origin,
        lookup_key: effectiveKey,
        email: form.email.trim().toLowerCase(),
        name: form.name || "",
        phone: form.phone || "",
        role_hint: plan.role,
        ref_code: refCode,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Não foi possível iniciar o pagamento");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao iniciar pagamento");
    } finally {
      setBusy(false);
    }
  };

  const currentUrl = typeof window !== "undefined"
    ? `${window.location.origin}/assinar${refCode ? `?ref=${refCode}` : ""}`
    : "";
  const copyLink = () => {
    navigator.clipboard?.writeText(currentUrl);
    toast.success("Link copiado! Cole no WhatsApp ou Instagram");
  };

  const whatsappMessage = encodeURIComponent(
    `🚛 *ACERTO RÁPIDO JVO* — o app do caminhoneiro brasileiro\n\n` +
    `✅ Acerto de viagem pronto em minutos\n` +
    `✅ GPS em tempo real dos caminhões\n` +
    `✅ Relatórios em PDF pro contador\n` +
    `✅ Cartão em até 3x ou *PIX com 25% OFF*\n\n` +
    `👉 Assine agora sem instalar nada:\n${currentUrl}`
  );
  const whatsappHref = `https://api.whatsapp.com/send?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={40} />
            <div className="hidden sm:block">
              <div className="font-extrabold text-blue-900 text-sm leading-none" style={{ fontFamily: "Manrope" }}>ACERTO RÁPIDO</div>
              <div className="text-[9px] tracking-[0.3em] text-blue-700/70">JVO</div>
            </div>
          </Link>
          <div className="flex gap-2 items-center">
            <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-blue-700 px-3">Entrar</Link>
            <Link to="/register"><Button variant="outline" className="text-sm h-9">Testar grátis 7d</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Badge className="bg-yellow-400 text-blue-900 mb-4 gap-1"><Lightning size={12} weight="fill" /> Assine agora · PIX ou cartão</Badge>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight" style={{ fontFamily: "Manrope" }}>
            Pagamento direto sem instalar nada
          </h1>
          <p className="text-blue-100 mt-3 text-base md:text-lg">
            Escolha o plano, pague no Stripe (PIX/Cartão) e sua conta é ativada automaticamente ao cadastrar.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-blue-50">
            <span className="flex items-center gap-1.5"><ShieldCheck size={16} weight="fill" /> Pagamento seguro Stripe</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> Cancele quando quiser</span>
            <span className="flex items-center gap-1.5"><Money size={16} weight="fill" /> Nota fiscal enviada por e-mail</span>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-extrabold" style={{ fontFamily: "Manrope" }}>1. Escolha o plano</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p) => {
              const isSel = selected === p.key;
              return (
                <button
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  onClick={() => setSelected(p.key)}
                  className={`text-left rounded-xl p-5 border-2 transition-all bg-white ${
                    isSel ? "border-blue-600 ring-4 ring-blue-100 shadow-lg scale-[1.02]" : "border-slate-200 hover:border-blue-300"
                  }`}
                >
                  {p.highlight && !isSel && (
                    <Badge className="bg-yellow-500 text-black mb-2 gap-1"><Star size={11} weight="fill" /> Mais escolhido</Badge>
                  )}
                  {isSel && <Badge className="bg-blue-600 text-white mb-2 gap-1"><CheckCircle size={11} weight="fill" /> Selecionado</Badge>}
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSel ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
                      <p.icon size={20} weight="duotone" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{p.audience}</div>
                  </div>
                  <div className="mt-3 text-lg font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{p.label}</div>
                  <div className="mt-2 text-3xl font-extrabold text-blue-700" style={{ fontFamily: "Manrope" }}>{p.price}</div>
                  <div className="text-xs text-slate-500">{p.period}</div>
                  {p.savings && <div className="mt-1 text-xs font-bold text-emerald-600">{p.savings}</div>}
                  {p.pix_price && (
                    <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-300 px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800 flex items-center gap-1">
                        <Lightning size={10} weight="fill" /> PIX à vista · 25% OFF
                      </div>
                      <div className="text-lg font-extrabold text-emerald-700 leading-tight" style={{ fontFamily: "Manrope" }}>{p.pix_price}</div>
                    </div>
                  )}
                  <ul className="mt-4 space-y-1.5">
                    {p.perks.map((t) => (
                      <li key={t} className="flex gap-2 text-xs text-slate-600">
                        <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> {t}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <Card className="p-6 md:p-8 border-2 border-blue-100 shadow-xl">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-extrabold" style={{ fontFamily: "Manrope" }}>2. Seus dados</h2>
              <p className="text-sm text-slate-600 mt-1">Vamos vincular o pagamento ao seu e-mail. Depois é só criar a conta com o mesmo e-mail.</p>
            </div>
            <form onSubmit={goPay} className="space-y-4">
              <div>
                <Label>E-mail *</Label>
                <Input
                  data-testid="public-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={setF("email")}
                  placeholder="seu@email.com"
                  className="mt-1 h-12"
                />
                <p className="text-xs text-slate-500 mt-1">É esse e-mail que você vai usar para criar sua conta depois.</p>
              </div>
              <div>
                <Label>Nome / Empresa</Label>
                <Input
                  data-testid="public-name"
                  value={form.name}
                  onChange={setF("name")}
                  placeholder={plan.role === "company" ? "Transportadora Silva LTDA" : "João Motorista"}
                  className="mt-1 h-12"
                />
              </div>
              <div>
                <Label>WhatsApp (opcional)</Label>
                <Input
                  data-testid="public-phone"
                  value={form.phone}
                  onChange={setF("phone")}
                  placeholder="(11) 99999-9999"
                  className="mt-1 h-12"
                />
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
                <plan.icon size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest font-bold text-blue-700">Você está assinando</div>
                  <div className="font-extrabold text-slate-900">{plan.label}</div>
                  <div className="text-sm text-slate-600">{effectivePrice} — {plan.period}</div>
                </div>
              </div>

              {/* Toggle Cartão / PIX 25% OFF — só aparece em planos anuais */}
              {plan.pix_key && (
                <div className="rounded-lg border-2 border-slate-200 p-1 grid grid-cols-2 gap-1 bg-slate-50">
                  <button
                    type="button"
                    data-testid="method-card"
                    onClick={() => setPixDiscount(false)}
                    className={`rounded-md py-2.5 px-3 text-sm font-bold transition-all ${
                      !pixDiscount ? "bg-white shadow-sm text-slate-900 border-2 border-blue-600" : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <CreditCard size={14} weight="fill" /> Cartão até 3x
                    </div>
                    <div className={`text-xs font-normal mt-0.5 ${!pixDiscount ? "text-slate-600" : "text-slate-400"}`}>{plan.price}</div>
                  </button>
                  <button
                    type="button"
                    data-testid="method-pix"
                    onClick={() => setPixDiscount(true)}
                    className={`rounded-md py-2.5 px-3 text-sm font-bold transition-all ${
                      pixDiscount ? "bg-emerald-100 shadow-sm text-emerald-900 border-2 border-emerald-600" : "text-slate-500 hover:text-emerald-700"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Lightning size={14} weight="fill" /> PIX 25% OFF
                    </div>
                    <div className={`text-xs font-normal mt-0.5 ${pixDiscount ? "text-emerald-800" : "text-slate-400"}`}>{plan.pix_price}</div>
                  </button>
                </div>
              )}

              <Button
                data-testid="public-pay-btn"
                type="submit"
                disabled={busy}
                className={`w-full h-14 text-white font-extrabold text-base gap-2 ${
                  pixDiscount ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {busy ? "Redirecionando..." : (
                  pixDiscount ? (<><Lightning size={20} weight="fill" /> Pagar {effectivePrice} no PIX <ArrowRight size={18} /></>) : (<><CreditCard size={20} weight="fill" /> Ir para pagamento seguro <ArrowRight size={18} /></>)
                )}
              </Button>
              <p className="text-center text-xs text-slate-500">
                Você será redirecionado ao <strong>Stripe</strong>. {pixDiscount ? "Pagamento à vista via PIX." : "Aceita PIX, cartão de crédito e boleto."}
              </p>
            </form>
          </Card>

          {/* Compartilhar */}
          <Card className="mt-6 p-5 bg-emerald-50 border-2 border-emerald-200">
            <div className="flex items-start gap-3">
              <WhatsappLogo size={28} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-emerald-900">Compartilhe este link</div>
                <p className="text-sm text-emerald-800 mt-0.5">
                  Repasse para clientes, motoristas ou parceiros — qualquer pessoa pode assinar direto sem precisar cadastrar antes.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    data-testid="copy-link-btn"
                    size="sm"
                    variant="outline"
                    onClick={copyLink}
                    className="gap-1.5 bg-white"
                  >
                    <Copy size={14} /> Copiar link
                  </Button>
                  <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="whatsapp-share-link">
                    <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <WhatsappLogo size={14} weight="fill" /> Enviar no WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Card>

          {/* Trial fallback */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-600">
              Prefere testar antes de pagar?{" "}
              <Link to="/register" className="text-blue-700 font-bold hover:underline">
                Criar conta com 7 dias grátis <ArrowRight size={12} className="inline" />
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={32} />
            <div>
              <div className="font-bold text-white text-sm">Acerto Rápido JVO</div>
              <div className="text-xs">Feito pra caminhoneiro brasileiro</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <Link to="/termos" className="hover:text-white">Termos</Link>
            <Link to="/privacidade" className="hover:text-white">Privacidade (LGPD)</Link>
            <Link to="/como-indicar" className="hover:text-white">Indique e ganhe</Link>
            <Link to="/login" className="hover:text-white">Entrar</Link>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Share FAB */}
      <a
        href={whatsappHref}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-fab"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 shadow-2xl shadow-emerald-600/40 transition-all hover:scale-105 group"
        title="Compartilhar no WhatsApp"
      >
        <WhatsappLogo size={26} weight="fill" />
        <span className="hidden sm:inline text-sm font-bold pr-1">Compartilhar</span>
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
        </span>
      </a>
    </div>
  );
}
